import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, FormControl, FormHelperText, Card, CardContent, CardHeader, Grid } from '@mui/material';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { useDispatch, useSelector } from 'react-redux';
import { usePostBillOperationMutation } from 'redux/api/operationApi';
import Webcam from 'react-webcam';
import { setCurrentOperation } from "redux/features/operationSlice";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import { useIntl } from 'react-intl';


const ScanDocumentComponent: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
  const dispatch = useDispatch();
    const { formatMessage } = useIntl();
  
  const [
    postBillOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      error: apiError,
    },
  ] = usePostBillOperationMutation();

  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;
  const isLocalhost = (IMAGE_SERVER ?? "").includes("localhost");
  const videoConstraints = {
    facingMode: isLocalhost ? 'user' : 'environment',
  };
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const webcamRef = useRef<Webcam>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };
  const operation = useSelector((state: any) => {
    return state.operationState?.currentOperation?.data || state.operationState?.currentOperation;
  });
  const base64ToBlob = (base64: string, contentType: string): Blob => {
    const byteString = atob(base64.split(",")[1]); // Decodifica la parte base64
    const mimeType =
      contentType ||
      base64.split(",")[0].match(/:(.*?);/)?.[1] ||
      "application/octet-stream";
    const arrayBuffer = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      arrayBuffer[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: mimeType });
  };
  const retakeImage = () => {
    setImgSrc(null);
    setError(false);
  };
  const handleSubmit = () => {
    if (!imgSrc) {
      setError(true);
      return;
    }

    if (!(IMAGE_SERVER && imgSrc.startsWith(IMAGE_SERVER))) {
      // Convertir base64 a Blob
      const imageBlob = base64ToBlob(imgSrc, "image/jpeg");
      // Crear FormData y añadir el archivo
      const formData = new FormData();
      formData.append(
        "salesDocument",
        imageBlob,
        `webcam-${Date.now()}.jpeg`
      );
      const operationData = {
        ...operation,
        salesDocument: imgSrc,
      };
      formData.append("data", JSON.stringify(operationData));
      postBillOperation({ id: operation.id, operationToUpdate: formData }).then(
        (res) => {
          if ("data" in res) {
            dispatch(setCurrentOperation(res.data));
            nextStep();
          }
        }
      );
    } else {
      nextStep();
    }
  };

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImgSrc(imageSrc);
    }
  }, [webcamRef]);

  return (
    <Card sx={{ p: 2, mt:2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {formatMessage({ id: "scan.document.title" })}
          </Typography>
        }
      />
      <CardContent>
        <Box
          sx={{
            maxWidth: '400px',
            margin: '0 auto',
            padding: '20px',
            backgroundColor: '',
            borderRadius: '8px',
          }}
        >

          {/* Área de documento */}
          <FormControl error={error}>
            {!imgSrc ? (
              <Box sx={{ position: "relative", width: "100%" }}>

                <Webcam
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  screenshotFormat="image/jpeg"
                  style={{
                    width: "100%",
                    height: "40vh",
                    borderRadius: "10px",
                    border: "2px solid #ccc",
                  }}
                />
                <CameraswitchIcon
                  onClick={toggleCamera}
                  color="secondary"
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    minWidth: "40px",
                    minHeight: "40px",
                    borderRadius: "50%",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <img
                  src={imgSrc}
                  alt="Captura de la webcam"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "10px",
                    marginTop: "10px",
                  }}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", mt: 2 }}>
              {!imgSrc ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capture}
                  style={{ width: "100%", fontSize: "16px" }}
                >
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    color="#FFF"
                    alignContent="center"
                  >
                    {formatMessage({ id: "buttons.take.picture" })}
                  </Typography>
                </Button>) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={retakeImage}
                  sx={{
                    width: "80%",
                    fontSize: 16,
                    padding: 1.25,
                  }}
                >
                  {formatMessage({ id: "buttons.take.again" })}
                </Button>
              )}
            </Box>

            <Grid container spacing={2} justifyContent="space-between">
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                  disabled={error || !imgSrc}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScanDocumentComponent;
